/**
 * Pure screen-space math for sketch-mode ruler strokes.
 *
 * Shift-constrained sketch lines snap in screen space so the user sees the same
 * 0/45/90 degree behavior in projected and screenspace sketch modes.
 */

export interface ScreenPoint {
  x: number;
  y: number;
}

export interface ScreenLineSample extends ScreenPoint {
  /** Normalized arc position along the sampled line, in [0, 1]. */
  t: number;
}

const TAU_8 = Math.PI / 4;

/**
 * Snap the endpoint of a screen-space segment to the nearest 45 degree bin
 * around `start`, preserving the original drag distance.
 */
export function snapScreenPointTo45(
  start: ScreenPoint,
  end: ScreenPoint,
): ScreenPoint {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const r = Math.hypot(dx, dy);
  if (r < 1e-9) return { x: end.x, y: end.y };
  const bin = Math.round(Math.atan2(dy, dx) / TAU_8);
  const theta = bin * TAU_8;
  return {
    x: start.x + r * Math.cos(theta),
    y: start.y + r * Math.sin(theta),
  };
}

/**
 * Return evenly spaced samples from start to end, inclusive. The segment count
 * is capped so live projected ruler strokes cannot explode into hundreds of
 * raycasts on high-resolution canvases.
 */
export function sampleScreenLine(
  start: ScreenPoint,
  end: ScreenPoint,
  spacingPx: number,
  maxSegments: number,
): ScreenLineSample[] {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-9) return [{ x: start.x, y: start.y, t: 0 }];

  const safeSpacing = Math.max(1, spacingPx);
  const safeMaxSegments = Math.max(1, Math.floor(maxSegments));
  const segmentCount = Math.min(
    safeMaxSegments,
    Math.max(1, Math.ceil(length / safeSpacing)),
  );
  const samples: ScreenLineSample[] = [];
  for (let i = 0; i <= segmentCount; i++) {
    const t = i / segmentCount;
    samples.push({
      x: start.x + dx * t,
      y: start.y + dy * t,
      t,
    });
  }
  return samples;
}
